/** Timing + motion helpers shared by the live preview and exporters. */

export interface PathSample {
  /** ms since capture started */
  t: number;
  /** divider x in canvas px */
  x: number;
}

export interface SmoothPath {
  /** total duration in ms */
  duration: number;
  /** evenly spaced divider positions from t=0 to t=duration */
  points: number[];
}

export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export const MIN_PATH_MS = 200;

/**
 * Turn raw drag samples (irregular timing, hand jitter) into a smooth,
 * evenly sampled path: resample at ~30fps, then moving-average with a
 * 9-sample window. Returns null when the capture is too short to use.
 */
export function buildSmoothPath(samples: readonly PathSample[]): SmoothPath | null {
  if (samples.length < 2) return null;
  const last = samples[samples.length - 1]!;
  const total = last.t;
  if (total < MIN_PATH_MS) return null;
  const stepMs = 33;
  const n = Math.max(2, Math.round(total / stepMs));
  const resampled: number[] = [];
  let si = 0;
  for (let i = 0; i <= n; i++) {
    const t = (i / n) * total;
    while (si < samples.length - 2 && samples[si + 1]!.t < t) si++;
    const a = samples[si]!;
    const b = samples[si + 1] ?? a;
    const span = b.t - a.t || 1;
    const frac = Math.max(0, Math.min(1, (t - a.t) / span));
    resampled.push(a.x + (b.x - a.x) * frac);
  }
  const win = 4;
  const smoothed = resampled.map((_, i) => {
    let sum = 0;
    let count = 0;
    for (let k = -win; k <= win; k++) {
      const idx = i + k;
      if (idx >= 0 && idx < resampled.length) {
        sum += resampled[idx]!;
        count++;
      }
    }
    return sum / count;
  });
  return { duration: total, points: smoothed };
}

/** Divider position at time `t` (ms), linearly interpolated, clamped to the path. */
export function samplePath(path: SmoothPath, t: number): number {
  const n = path.points.length - 1;
  const frac = Math.max(0, Math.min(1, t / path.duration));
  const idx = frac * n;
  const i0 = Math.floor(idx);
  const i1 = Math.min(n, i0 + 1);
  const localFrac = idx - i0;
  const p0 = path.points[i0]!;
  const p1 = path.points[i1]!;
  return p0 + (p1 - p0) * localFrac;
}
